import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { registrationNumber as registrationNumberValidator } from '@klarna/mf-validations';
import {
  IconInformation,
  BorderlessDropdown,
  SpacerVertical,
  SpacerHorizontal,
} from '@klarna/bubble-ui';
import { selectors as i18nSelectors, translator } from 'mage-i18n';
import {
  Alert,
  defaultContainerPropTypes,
  formUtils,
  FieldsetForObject,
  HorizontalWrapper,
  Input,
  payloadUtils,
  TOOLTIP_MODES,
} from 'moet-core';

import { OPTIONS_META } from '@/constants/countries';
import { VerticalRhythm } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { validators } from '@/flow/validation';
import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  ContentAnimationWrapper,
} from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { logEvent } from '@/utils/analytics';
import { CAPS } from '@/constants';
import {
  getOrganizationEntityTypesInMerchantCountry,
  getMerchantAwareTranslator,
  getMerchantAwareTranslationKey,
} from '@/selectors/merchant';

import { CY_COMPANY_REGISTRATION_ID_IDS } from '../constants';
import { getDefaultState } from '../selectors';
import { ANALYTICS_EVENTS } from '../analyticsEvents';

const tPrefix = 'products-fe.containers.tax_identification_number';
export class TaxIdentificationNumber extends Component {
  constructor(props) {
    super(props);

    const {
      t,
      tMerchantAware,
      organizationEntityTypes,
      organizationEntityKeys,
      isDisabled,
      isLoading = true,
      country,
      defaultState,
      language,
    } = props;

    const { registrationNumber = '' } = defaultState || {};

    const state = {
      language,
      organizationEntityKeys,
      fields: formUtils.initForm({
        organizationEntityType: {
          component: BorderlessDropdown,
          size: 1,
          label: tMerchantAware(`${tPrefix}.fields.organization_entity.label`),
          value: this.getInitialEntityType(),
          isInitialized: false,
          isValid: false,
          validators: [
            {
              callback: validators.common.isOneOf,
              errorMessage: t(`${tPrefix}.fields.organization_entity.is_one_of`),
              stateSelector: ({ organizationEntityKeys = [] }) => ({
                subject: organizationEntityKeys,
              }),
            },
          ],
          extraProps: {
            options: organizationEntityTypes,
            loading: isLoading,
            disabled: isDisabled,
            ...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.INPUT_ENTITY_TYPE, window),
          },
        },
        registrationNumber: {
          component: Input,
          size: 1,
          type: 'text',
          label: tMerchantAware(`${tPrefix}.fields.registration_number.label`),
          validators: [
            {
              callback: registrationNumberValidator.validate,
              errorMessage: t(`${tPrefix}.fields.registration_number.errors.general`),
              stateSelector: state => ({
                optional:
                  _get(state, 'fields.organizationEntityType.value') === 'SOLE_PROPRIETORSHIP',
              }),
              options: { country },
            },
          ],
          extraProps: {
            autoFocus: true,
            tooltipMode: TOOLTIP_MODES.HELPER,
            tooltipText: t(`${tPrefix}.fields.registration_number.errors.invalid`),
            ...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.INPUT_REGISTRATION_NUMBER, window),
          },
          isInitialized: false,
          isValid: false,
          value: registrationNumber,
        },
      }),
    };

    this.state = { ...state, initialState: state };

    this.onChangeFields = this.onChangeFields.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.getBody = this.getBody.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  static updateTranslation(key, props) {
    const {
      organizationEntityTypes = [],
      isLoading,
      isDisabled,
      getTranslationKey,
      t,
      country,
    } = props;

    const fieldTranslationsMap = {
      organizationEntityType: {
        label: getTranslationKey(`${tPrefix}.fields.organization_entity.label`),
        validators: [
          {
            callback: validators.common.isOneOf,
            errorMessage: `${tPrefix}.fields.organization_entity.errors.is_one_of`,
            stateSelector: ({ organizationEntityKeys = [] }) => ({
              subject: organizationEntityKeys,
            }),
          },
        ],
        extraProps: {
          options: organizationEntityTypes,
          loading: isLoading,
          disabled: isDisabled,
          ...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.INPUT_ENTITY_TYPE, window),
        },
        resetFields: ['registrationNumber'],
      },
      registrationNumber: {
        label: getTranslationKey(`${tPrefix}.fields.registration_number.label`),
        validators: [
          {
            callback: registrationNumberValidator.validate,
            errorMessage: getTranslationKey(`${tPrefix}.fields.registration_number.errors.general`),
            options: { country },
            stateSelector: state => ({
              optional:
                _get(state, 'fields.organizationEntityType.value') === 'SOLE_PROPRIETORSHIP',
            }),
          },
        ],
        extraProps: {
          autoFocus: true,
          tooltipMode: TOOLTIP_MODES.HELPER,
          tooltipText: t(`${tPrefix}.fields.registration_number.errors.invalid`),
          ...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.INPUT_REGISTRATION_NUMBER, window),
        },
      },
    };

    return formUtils.updateTranslatedFields(fieldTranslationsMap, key, t);
  }

  getInitialEntityType() {
    const {
      defaultState: { organizationEntityType = '' } = {},
      organizationEntityKeys = [],
    } = this.props;

    return organizationEntityKeys.includes(organizationEntityType)
      ? organizationEntityType
      : undefined;
  }

  static getDerivedStateFromProps(props, state) {
    const { language, organizationEntityKeys } = props;
    const newState = formUtils.initFormFromState({
      ...state,
      language,
      organizationEntityKeys,
      fields: {
        ...state.fields,
        organizationEntityType: {
          ...state.fields.organizationEntityType,
          ...TaxIdentificationNumber.updateTranslation('organizationEntityType', props),
        },
        registrationNumber: {
          ...state.fields.registrationNumber,
          ...TaxIdentificationNumber.updateTranslation('registrationNumber', props),
        },
      },
    });

    return {
      ...newState,
      initialState: state.initialState,
    };
  }

  onChangeFields(fields) {
    this.setState({ fields });
  }

  onBlur(key) {
    formUtils.onBlurFields(key, this.state, this.onChangeFields);
  }

  onChange(key, val) {
    formUtils.onChangeFields(key, val, this.state, this.onChangeFields);
  }

  getBody({ fields }) {
    return {
      organizationEntityType: fields.organizationEntityType.value,
      subjectToVat: false,
      registered: fields.organizationEntityType.value !== 'SOLE_PROPRIETORSHIP',
      registrationId: fields.registrationNumber.value,
    };
  }

  onNext() {
    this.setState({ ...this.state, fields: formUtils.initializeFields(this.state.fields) });

    formUtils.callToActionOnNextWrapper(this.state.fields, () => {
      const capabilityPayload = payloadUtils.getMerchantCapabilityPayload(
        this.props.mid,
        this.state,
        this.getBody,
        'TaxIdentificationNumber',
      );
      this.props.onNext(capabilityPayload);
      logEvent(ANALYTICS_EVENTS.companyRegistrationInformation, {
        ...capabilityPayload.payload.body,
      });
    });
  }

  render() {
    const {
      country,
      error,
      hasBack,
      isLoading,
      isDisabled,
      onBack,
      t,
      tMerchantAware,
    } = this.props;
    const { fields } = this.state;
    const organizationEntityTypeFields = {
      organizationEntityType: fields.organizationEntityType,
    };

    const registrationNumberFields = {
      registrationNumber: fields.registrationNumber,
    };

    const tabIndex = Object.keys(fields).length + 1;

    const info = country === 'US' && tMerchantAware(`${tPrefix}.info`);
    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
      >
        <FieldsetForObject
          isBorderless
          name="forced-registration-information"
          fields={organizationEntityTypeFields}
          onBlur={this.onBlur}
          onChange={this.onChange}
          isDisabled={isDisabled}
        />
        <SpacerVertical small />
        {fields.organizationEntityType.value &&
          fields.organizationEntityType.value !== 'SOLE_PROPRIETORSHIP' && (
            <ContentAnimationWrapper>
              <FieldsetForObject
                isBorderless
                name="forced-registration-information"
                fields={registrationNumberFields}
                onBlur={this.onBlur}
                onChange={this.onChange}
                isDisabled={isDisabled}
              />
              {info && (
                <>
                  <VerticalRhythm spacing={{ mobile: 3 }} />
                  <Alert content={info} icon={IconInformation} />
                </>
              )}
            </ContentAnimationWrapper>
          )}

        <VerticalRhythm spacing={{ mobile: 3 }} />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton tabIndex={tabIndex + 1} onClick={onBack} disabled={isDisabled}>
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal medium />
            </>
          )}
          <CallToActionButton
            tabIndex={tabIndex}
            loading={isLoading}
            onClick={this.onNext}
            disabled={isDisabled}
            {...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

TaxIdentificationNumber.propTypes = {
  ...defaultContainerPropTypes,
  country: PropTypes.string.isRequired,
  getTranslationKey: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

const WrappedComponent = compose(
  connect(state => {
    const organizationEntityTypes = getOrganizationEntityTypesInMerchantCountry(state);

    return {
      defaultState: getDefaultState(state),
      language: i18nSelectors.language(state),
      t: translator(state),
      tMerchantAware: getMerchantAwareTranslator(state),
      organizationEntityTypes,
      organizationEntityKeys: formUtils.getOptionKeys(organizationEntityTypes),
      getTranslationKey: getMerchantAwareTranslationKey(state),
    };
  }),
  withCapabilityBeforeNext({ cap: CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH }),
)(TaxIdentificationNumber);

WrappedComponent.variantId = OPTIONS_META.REGISTRATION_ID_KIND.OPTIONS.TIN;
WrappedComponent.onboardingTypes = '*';

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'businessDetails',
    fields: {
      registered: {},
      registrationId: {},
      organizationEntityType: {},
    },
  },
];

export default WrappedComponent;
